import { z } from 'zod';
import { daysBetween } from './dates.js';
/**
 * ¿La fecha propuesta está dentro de la tolerancia? (apartado 6.4)
 *
 * La tolerancia es simétrica: entregar cinco días antes también es una
 * desviación que compras puede querer ver (ocupa playa y almacén antes de
 * tiempo). Si IMAR quiere tolerar solo los adelantos, se cambia aquí.
 */
export function checkDate(required, proposed, toleranceDays) {
    const deviationDays = daysBetween(required, proposed);
    return { deviationDays, withinTolerance: Math.abs(deviationDays) <= toleranceDays };
}
/**
 * ¿El precio del albarán está dentro de la tolerancia? (apartado 6.5)
 *
 * Un pedido a precio 0 no tiene desviación porcentual que calcular: cualquier
 * precio distinto de 0 se considera fuera de tolerancia y va a compras, que es
 * lo prudente. Dividir por cero daría Infinity y, según el signo, lo dejaría
 * pasar.
 */
export function checkPrice(orderPrice, invoicedPrice, tolerancePct) {
    const exact = Math.abs(orderPrice - invoicedPrice) < 0.005;
    if (exact)
        return { deviationPct: 0, withinTolerance: true, exact: true };
    if (orderPrice === 0)
        return { deviationPct: Number.POSITIVE_INFINITY, withinTolerance: false, exact: false };
    const deviationPct = ((invoicedPrice - orderPrice) / orderPrice) * 100;
    // Redondeo a 4 decimales antes de comparar: 5,0000001 % por coma flotante no
    // debe sacar de tolerancia un precio que está justo en el 5 %.
    const rounded = Math.round(deviationPct * 10_000) / 10_000;
    return { deviationPct: rounded, withinTolerance: Math.abs(rounded) <= tolerancePct, exact: false };
}
/** Un precio o una fecha validados, por la tolerancia o por compras. */
export function isValidated(status) {
    return status === 'A' || status === 'C';
}
/**
 * Luz verde a la entrada de mercancía (apartado 6.5): precio validado Y
 * recepción física confirmada. Las dos; ninguna sustituye a la otra.
 */
export function greenLight(priceStatus, physicalReceipt) {
    return isValidated(priceStatus) && physicalReceipt;
}
/**
 * Franja de urgencia de una línea para el código de colores (apartado 6.1).
 *
 * La fecha que manda es la comprometida si la hay, y si no la de entrega de
 * SAP: es la que el proveedor se ha comprometido a cumplir, o en su defecto la
 * que IMAR necesita.
 */
export function lineUrgency(line, today, soonDays = 15) {
    if (!line.open)
        return 'done';
    const due = line.confirmedDate ?? line.deliveryDate ?? line.requiredDate;
    if (due === null)
        return 'later';
    const days = daysBetween(today, due);
    if (days < 0)
        return 'late';
    if (days <= soonDays)
        return 'soon';
    return 'later';
}
const URGENCY_RANK = { late: 0, soon: 1, later: 2, done: 3 };
export function worstUrgency(values) {
    return values.reduce((worst, u) => (URGENCY_RANK[u] < URGENCY_RANK[worst] ? u : worst), 'done');
}
// ── Entradas del portal ───────────────────────────────────────────────────
const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Fecha en formato AAAA-MM-DD');
export const rejectOrderSchema = z.object({
    reason: z.string().trim().min(3, 'Indica el motivo del rechazo').max(254),
});
export const proposeDatesSchema = z.object({
    lines: z
        .array(z.object({ lineNum: z.number().int().min(0), date: day }))
        .min(1, 'Indica al menos una fecha'),
});
export const deliveryNoteSchema = z.object({
    number: z.string().trim().min(1, 'Indica el número de albarán').max(50),
    lines: z
        .array(z.object({ lineNum: z.number().int().min(0), price: z.number().min(0).max(1e9) }))
        .min(1, 'Indica el precio de al menos una línea'),
    description: z.string().trim().max(254).optional().default(''),
});
export const reviewSchema = z.object({
    decision: z.enum(['approve', 'reject']),
    comment: z.string().trim().max(254).optional().default(''),
});
//# sourceMappingURL=portal.js.map